import './Homepage.scss';

import Table, { Order } from 'components/Table/Table';
import { useGetDashboardReports } from 'hooks/CodegenDashboardHooks';

import { DefaultRoutes } from 'Routes';
import ReportTypesTile from 'components/Report/ReportTypesTile/ReportTypesTile';
import { Typography } from '@trackman/web-shared-components';
import { displayDate } from 'utilities/dateHelpers';
import { useNavigate } from 'react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportItemFilterDto, ReportSubType, ReportType } from 'types/CodegenDashboardTypes';
import { GrouppedChip } from 'components/SearchInputs/SearchChips/SearchChips';
import { useApplicationUserRightsStore } from 'stores/ApplicationUserRightsStore';
import { AppPermissions } from 'constants/AppPermissions';
import { useStores } from 'index';

const reportTypeMap: Record<string, ReportType> = {
  [AppPermissions.PlayerReports]: 'PlayerReport',
  [AppPermissions.SessionReports]: 'SessionReviewReport',
  [AppPermissions.UmpireReports]: 'UmpireReport',
  [AppPermissions.PracticeReports]: 'PracticeReport',
};

const reportSubTypeMap: Record<string, ReportSubType[]> = {
  [AppPermissions.PlayerReports]: ['Batter', 'Pitcher'],
  [AppPermissions.SessionReports]: ['BatterSingleSession', 'BatterAggregated', 'PitcherSingleSession', 'PitcherAggregated'],
  [AppPermissions.UmpireReports]: ['SingleSession', 'AllSessions'],
  [AppPermissions.PracticeReports]: ['BatterSingleSession', 'BatterAggregated', 'PitcherSingleSession', 'PitcherAggregated'],
};

const reportTypeOrder = [
  AppPermissions.PlayerReports,
  AppPermissions.SessionReports,
  AppPermissions.PracticeReports,
  AppPermissions.UmpireReports,
];

// TODO: Remove this when Umpire Admin role is implemented
const umpireOrgIds = [
  'e8d3719c-0de7-4c11-310c-08dd3fdcf183',
  'cf39442e-698d-4be0-e8b2-08dc26970a37',
  'fa1838d2-05e3-4ed6-310e-08dd3fdcf183',
  '3cc679b4-80e9-418e-e8b1-08dc26970a37',
  'c5b6bd22-9488-40ca-0c63-08d9cfbc0a60',
  '840aa1f9-a5e9-4e7f-e8af-08dc26970a37',
  '40b1c442-c8b6-484b-a243-7a1f43a8501c',
  '5703c5ac-6b09-4a73-2d21-08de67fff4b3',
  '64c8ef39-ef6a-4ddc-2f90-08de7616c4ff',
  'fabb3d6b-f9f6-485c-310f-08dd3fdcf183',
];

const Homepage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: recentReports, isLoading: isLoadingRecentReports } = useGetDashboardReports();
  const { permissions, isAdmin } = useApplicationUserRightsStore();
  const { organizationId } = useStores().me; // TODO: Remove this when Umpire Admin role is implemented

  const availableReportTypes: { reportType: ReportType; reportSubTypes: ReportSubType[] }[] = useMemo(() => {
    const items = permissions
      .filter((permission) => reportTypeOrder.includes(permission))
      .sort((a, b) => reportTypeOrder.indexOf(a) - reportTypeOrder.indexOf(b))
      .map((permission) => ({
        reportType: reportTypeMap[permission],
        reportSubTypes: [...reportSubTypeMap[permission]],
      }));

    // TODO: Remove && part when Umpire Admin role is implemented
    if (isAdmin && umpireOrgIds.includes(organizationId)) {
      items.find((item) => item.reportType === 'UmpireReport')?.reportSubTypes.push('AdminOverview');
    }

    return items;
  }, [permissions, isAdmin]);

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Exclude<typeof recentReports, undefined>[number] | ''>('generatedAt');

  return (
    <div className='container flex-column pd-4 gap-4'>
      <div className='report-tiles-container'>
        {availableReportTypes?.map((report) => <ReportTypesTile key={report.reportType} report={report} />) ??
          [0, 1].map((skeleton) => <ReportTypesTile key={skeleton} />)}
      </div>

      <div className='generated-reports-container'>
        <Typography variant='h2'>{t('Last generated reports')}</Typography>
        <Table
          rows={recentReports ?? []}
          pageState={[page, setPage]}
          orderState={[order, setOrder]}
          orderByState={[orderBy, setOrderBy]}
          columns={[
            {
              field: 'reportType',
              headerName: t('Report'),
              renderCell: (_, row) => <span style={{ whiteSpace: 'nowrap' }}>{t(`${row.reportType}.${row.reportSubType}`)}</span>,
            },
            {
              field: 'reportFilter',
              headerName: t('Time period'),
              renderCell: (filter: ReportItemFilterDto) =>
                filter.seasonNames?.length ? (
                  <GrouppedChip groupName={t('chip.season')} label={filter.seasonNames} />
                ) : (
                  filter.dateRanges && (
                    <>
                      <GrouppedChip groupName={t('chip.from')} label={displayDate(filter.dateRanges[0].utcDateFrom)} />
                      <GrouppedChip groupName={t('chip.to')} label={displayDate(filter.dateRanges[0].utcDateTo)} />
                    </>
                  )
                ),
            },
            {
              field: 'reportFilter',
              headerName: `${t('League')} / ${t('Level')}`,
              renderCell: (filter: ReportItemFilterDto) =>
                filter.level?.name ? (
                  <GrouppedChip label={filter.level.name} />
                ) : filter.leagues?.length ? (
                  <GrouppedChip groupName={t('chip.leagueNames')} label={filter.leagues.map((x) => x.name)} />
                ) : (
                  <></>
                ),
            },
            {
              field: 'reportFilter',
              headerName: t('Team'),
              renderCell: (filter: ReportItemFilterDto) => filter.team?.name && <GrouppedChip label={filter.team?.name} />,
            },
            {
              field: 'reportFilter',
              headerName: t('Participants'),
              renderCell: (filter: ReportItemFilterDto) =>
                filter.players.length ? (
                  <GrouppedChip groupName={t('chip.playerNames')} label={filter.players.map((x) => x.name)} />
                ) : filter.umpires.length ? (
                  <GrouppedChip groupName={t('chip.umpireNames')} label={filter.umpires.map((x) => x.name)} />
                ) : (
                  <></>
                ),
            },
            {
              field: 'generatedAt',
              headerName: t('generatedAt'),
              renderCell: (cellValue) => displayDate(cellValue),
            },
          ]}
          loading={isLoadingRecentReports}
          noRowsLabel={t('No reports found.')}
          onRowClick={(row) => {
            const reportRoute = DefaultRoutes[row.reportType][row.reportSubType];
            if (reportRoute) navigate(`${reportRoute}/${row.reportId}`, { viewTransition: true });
          }}
        />
      </div>
    </div>
  );
};

export default Homepage;
